import { useMemo } from "react";
import { useNavigate } from "@tanstack/react-router";
import { Star } from "lucide-react";
import { getPointsBadge } from "@/lib/utils";
import { SortOption, SortDirection } from "./PlayersSort";
import { Player } from "@/api/playerList";
import { Loader } from "../common/loader";
import MaterialIcon from "@/components/general/common/MaterialIcon";
import { PlayerAvatar } from "@/components/general/common/PlayerAvatar";

interface PlayersListProps {
    sortOption: SortOption;
    sortDirection: SortDirection;
    players: Player[];
    isLoading: boolean;
    noWrapper?: boolean;
    title?: string;
    teamId?: number;
}

const ROLE_LABELS: Record<string, string> = {
    Attacker: "Forward",
    Defender: "Defense",
    Goalkeeper: "Goalie",
    Mixed: "Mixed",
};

function getRoleLabel(role?: string[]) {
    if (!role || role.length === 0) return "Player";
    if (role.length === 1) return ROLE_LABELS[role[0]] ?? role[0];
    return role.join("/");
}

export function PlayersList({ sortOption, sortDirection, players, isLoading, title, teamId }: PlayersListProps) {
    const navigate = useNavigate();

    const topPlayerId = useMemo(() => {
        if (!players.length) return undefined;
        return players.reduce((best, p) => (p.averageKpi > best.averageKpi ? p : best), players[0]).id;
    }, [players]);

    const sortedPlayers = useMemo(() => {
        const sorted = [...players];

        switch (sortOption) {
            case "alphabetical":
                sorted.sort((a, b) => `${a.firstName} ${a.lastName}`.localeCompare(`${b.firstName} ${b.lastName}`));
                break;
            case "averageKPI":
                sorted.sort((a, b) => b.averageKpi - a.averageKpi);
                break;
            case "role":
                sorted.sort((a, b) => {
                    const aRole = a.role?.join("/") ?? "";
                    const bRole = b.role?.join("/") ?? "";
                    if (aRole !== bRole) {
                        if (!aRole && bRole) return 1;
                        if (aRole && !bRole) return -1;
                        return aRole.localeCompare(bRole);
                    }
                    return b.averageKpi - a.averageKpi;
                });
                break;
        }

        if (sortDirection === "asc") return sorted.reverse();
        return sorted;
    }, [players, sortOption, sortDirection]);

    const handlePlayerClick = (playerId: number) => {
        navigate({ to: `/player/${playerId}` });
    };

    if (players.length === 0 && isLoading) {
        return (
            <div className="py-6 flex justify-center">
                <Loader message="Loading players..." />
            </div>
        );
    }

    if (players.length === 0 && !isLoading) {
        return null;
    }

    return (
        <div>
            {title && <h2 className="text-xl font-headline font-bold text-primary mb-4 mt-2">{title}</h2>}
            <div
                id={title === "Goalkeepers" ? "onboarding-players-goalkeepers" : "onboarding-players-field"}
                className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-2 md:gap-6 pb-6"
            >
                {sortedPlayers.map((player) => {
                    const isElite = player.id === topPlayerId;
                    const { bg: kpiBg, text: kpiText, border: kpiBorder } = getPointsBadge(player.averageKpi);

                    const c = isElite
                        ? {
                              card: "bg-primary shadow-lg border-secondary-token",
                              avatarBg: "bg-white/10 border-white/20",
                              avatarVariant: "dark" as const,
                              roleBg: "bg-[#ba0a06] text-white",
                              name: "text-white",
                              sub: "text-white/70",
                              kpiLabel: "text-white/70",
                              statLabel: "text-white/40",
                              statValue: "text-white",
                              divider: "border-white/10",
                          }
                        : {
                              card: "bg-white shadow-sm border-primary",
                              avatarBg: "bg-surface-container-low border-outline-variant/20",
                              avatarVariant: "light" as const,
                              roleBg: "bg-surface-container-highest text-primary",
                              name: "text-primary",
                              sub: "text-on-surface-variant",
                              kpiLabel: "text-on-surface-variant",
                              statLabel: "text-slate-400",
                              statValue: "text-primary",
                              divider: "border-surface-container-highest",
                          };

                    return (
                        <div
                            key={player.id}
                            onClick={() => handlePlayerClick(player.id)}
                            className={`rounded-lg border-l-4 overflow-hidden group hover:shadow-xl transition-all duration-300 cursor-pointer ${c.card}`}
                        >
                            <div className="p-3 md:p-6">
                                <div className="flex items-center gap-3 md:hidden">
                                    <div
                                        className={`w-12 h-12 rounded-lg overflow-hidden border shrink-0 ${c.avatarBg}`}
                                    >
                                        <PlayerAvatar
                                            playerId={player.id}
                                            teamId={player.teamId ?? teamId}
                                            firstName={player.firstName}
                                            lastName={player.lastName}
                                            variant={c.avatarVariant}
                                        />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-baseline gap-1.5 min-w-0 mb-0.5">
                                            <h3 className={`font-bold font-headline leading-tight truncate ${c.name}`}>
                                                {player.firstName} {player.lastName}
                                            </h3>
                                            <span
                                                className={`inline-block px-1.5 py-0.5 text-[9px] font-bold rounded-sm uppercase shrink-0 ${c.roleBg}`}
                                            >
                                                {getRoleLabel(player.role)}
                                            </span>
                                        </div>
                                        <p className={`text-xs flex items-center gap-1 ${c.sub}`}>
                                            <MaterialIcon name="history" className="text-xs" />
                                            vs. {player.lastOpponent}
                                        </p>
                                    </div>
                                    <div className="shrink-0 flex items-center gap-1.5">
                                        {isElite && <Star className="w-4 h-4 text-secondary fill-secondary" />}
                                        <div
                                            className={`inline-flex items-center px-2 py-0.5 ${kpiBg} ${kpiText} rounded font-bold text-sm border ${kpiBorder}`}
                                        >
                                            {player.averageKpi.toFixed(1)}
                                        </div>
                                    </div>
                                </div>

                                <div className="hidden md:block">
                                    <div className="flex justify-between items-start mb-6">
                                        <div
                                            className={`w-20 h-20 rounded-lg overflow-hidden border shrink-0 group-hover:scale-110 transition-transform duration-500 ${c.avatarBg}`}
                                        >
                                            <PlayerAvatar
                                                playerId={player.id}
                                                teamId={player.teamId ?? teamId}
                                                firstName={player.firstName}
                                                lastName={player.lastName}
                                                variant={c.avatarVariant}
                                            />
                                        </div>
                                        <div className="flex flex-col items-end gap-1">
                                            <div className="flex items-center gap-1.5">
                                                {isElite && <Star className="w-4 h-4 text-secondary fill-secondary" />}
                                                <div
                                                    className={`inline-flex items-center px-2.5 py-1 ${kpiBg} ${kpiText} rounded font-bold text-xl border ${kpiBorder}`}
                                                >
                                                    {player.averageKpi.toFixed(1)}
                                                </div>
                                            </div>
                                            <div
                                                className={`text-[10px] uppercase tracking-wider font-bold ${c.kpiLabel}`}
                                            >
                                                KPI Score
                                            </div>
                                        </div>
                                    </div>
                                    <div>
                                        <div className="flex items-baseline gap-2 mb-1">
                                            <h3 className={`text-xl font-bold font-headline leading-tight ${c.name}`}>
                                                {player.firstName} {player.lastName}
                                            </h3>
                                            <span
                                                className={`inline-block px-2 py-0.5 text-[10px] font-bold rounded-sm uppercase shrink-0 ${c.roleBg}`}
                                            >
                                                {getRoleLabel(player.role)}
                                            </span>
                                        </div>
                                        <p className={`text-sm mt-1 flex items-center gap-1 ${c.sub}`}>
                                            <MaterialIcon name="history" className="text-xs" />
                                            vs. {player.lastOpponent}
                                        </p>
                                    </div>
                                    <div className={`mt-6 pt-6 border-t grid grid-cols-2 gap-4 ${c.divider}`}>
                                        <div>
                                            <div className={`text-[10px] uppercase font-bold ${c.statLabel}`}>
                                                Avg KPI
                                            </div>
                                            <div className={`text-lg font-bold ${c.statValue}`}>
                                                {player.averageKpi.toFixed(1)}
                                            </div>
                                        </div>
                                        <div>
                                            <div className={`text-[10px] uppercase font-bold ${c.statLabel}`}>
                                                Best KPI
                                            </div>
                                            <div className={`text-lg font-bold ${c.statValue}`}>
                                                {player.bestKpi.toFixed(1)}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}
